import React, { useState, useRef } from 'react';
import {
  Box, Container, Paper, Typography, TextField, MenuItem,
  Button, Grid, Chip, InputAdornment, Fade, CircularProgress, Alert
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import QuizIcon from '@mui/icons-material/Quiz';
import PersonIcon from '@mui/icons-material/Person';
import MicIcon from '@mui/icons-material/Mic';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const INTERVIEW_TYPES = [
  { value: 'behavioral', label: 'Behavioral', desc: 'STAR-method, past experiences' },
  { value: 'technical', label: 'Technical', desc: 'Skills, problem-solving' },
  { value: 'case', label: 'Case Study', desc: 'Business scenarios, frameworks' },
  { value: 'general', label: 'General', desc: 'Mixed, career-focused' },
];

const TOPIC_SUGGESTIONS = {
  behavioral: ['Leadership', 'Conflict resolution', 'Teamwork', 'Failure & learning', 'Communication', 'Time management'],
  technical: ['System design', 'Data structures', 'Algorithms', 'Past projects', 'Architecture', 'Debugging'],
  case: ['Market sizing', 'Profitability', 'Strategy', 'Competitive analysis', 'Operations', 'M&A'],
  general: ['Career goals', 'Motivation', 'Strengths & weaknesses', 'Culture fit', 'Achievements'],
};

export default function SetupScreen({ onComplete }) {
  const [form, setForm] = useState({
    role: '',
    company: '',
    interviewType: 'behavioral',
    topics: '',
    interviewerName: '',
    interviewerPersona: '',
    jobDescription: '',
  });
  const [errors, setErrors] = useState({});
  const [jdExtracting, setJdExtracting] = useState(false);
  const [jdError, setJdError] = useState('');
  const fileInputRef = useRef(null);

  function handleChange(field) {
    return (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  function addTopic(topic) {
    const current = form.topics ? form.topics.split(',').map(t => t.trim()).filter(Boolean) : [];
    if (!current.includes(topic)) {
      const updated = [...current, topic].join(', ');
      setForm(f => ({ ...f, topics: updated }));
    }
  }

  async function handleJdImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setJdError('');
    setJdExtracting(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await fetch('/api/mi/extract-jd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64, mediaType: file.type }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Extraction failed');
      setForm(f => ({ ...f, jobDescription: data.text }));
    } catch (err) {
      setJdError(err.message);
    } finally {
      setJdExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function validate() {
    const e = {};
    if (!form.role.trim()) e.role = 'Role is required';
    if (!form.company.trim()) e.company = 'Company is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    onComplete(form);
  }

  const suggestions = TOPIC_SUGGESTIONS[form.interviewType] || [];

  return (
    <Fade in>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6 }}>
        <Container maxWidth="md">
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 64, height: 64, borderRadius: '50%',
              bgcolor: 'primary.main', mb: 2,
              boxShadow: '0 0 32px rgba(99,102,241,0.4)'
            }}>
              <MicIcon sx={{ fontSize: 32, color: '#fff' }} />
            </Box>
            <Typography variant="h4" gutterBottom>MockInterview</Typography>
            <Typography variant="body1" color="text.secondary">
              Practice your interview with an AI interviewer — by voice
            </Typography>
          </Box>

          <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Set up your interview
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Role */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Role / Position"
                    value={form.role}
                    onChange={handleChange('role')}
                    error={!!errors.role}
                    helperText={errors.role || 'e.g. Software Engineer, Product Manager'}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><WorkIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>
                    }}
                  />
                </Grid>

                {/* Company */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Company"
                    value={form.company}
                    onChange={handleChange('company')}
                    error={!!errors.company}
                    helperText={errors.company || 'e.g. Google, McKinsey, your dream company'}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>
                    }}
                  />
                </Grid>

                {/* Interview Type */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                    Interview type
                  </Typography>
                  <Grid container spacing={1.5}>
                    {INTERVIEW_TYPES.map(type => (
                      <Grid item xs={6} sm={3} key={type.value}>
                        <Box
                          onClick={() => setForm(f => ({ ...f, interviewType: type.value }))}
                          sx={{
                            p: 1.5, borderRadius: 2, cursor: 'pointer', textAlign: 'center',
                            border: '2px solid',
                            borderColor: form.interviewType === type.value ? 'primary.main' : 'divider',
                            bgcolor: form.interviewType === type.value ? 'rgba(99,102,241,0.1)' : 'transparent',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(99,102,241,0.05)' }
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>{type.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{type.desc}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                {/* Topics */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth multiline rows={2}
                    label="Topics / Focus areas (optional)"
                    value={form.topics}
                    onChange={handleChange('topics')}
                    helperText="Comma-separated list of areas you want to practise"
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ mt: 1, alignSelf: 'flex-start' }}><QuizIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>
                    }}
                  />
                  {suggestions.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {suggestions.map(s => (
                        <Chip
                          key={s} label={s} size="small" variant="outlined"
                          onClick={() => addTopic(s)}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(99,102,241,0.1)' } }}
                        />
                      ))}
                    </Box>
                  )}
                </Grid>

                {/* Interviewer Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Interviewer name (optional)"
                    value={form.interviewerName}
                    onChange={handleChange('interviewerName')}
                    helperText="e.g. Sarah, Dr. Chen — leave blank for 'Alex'"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>
                    }}
                  />
                </Grid>

                {/* Interviewer Persona */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Interviewer style (optional)"
                    value={form.interviewerPersona}
                    onChange={handleChange('interviewerPersona')}
                    helperText="e.g. Very direct and challenging, Warm and encouraging"
                  />
                </Grid>

                {/* Job Description */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Job description (optional)
                    </Typography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleJdImageUpload}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={jdExtracting ? <CircularProgress size={14} /> : <UploadFileIcon fontSize="small" />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={jdExtracting}
                      sx={{ ml: 'auto', fontSize: '0.75rem' }}
                    >
                      {jdExtracting ? 'Extracting…' : 'Upload screenshot'}
                    </Button>
                  </Box>
                  {jdError && <Alert severity="error" sx={{ mb: 1 }}>{jdError}</Alert>}
                  <TextField
                    fullWidth multiline rows={4}
                    label="Paste job description"
                    value={form.jobDescription}
                    onChange={handleChange('jobDescription')}
                    helperText="Paste the JD or upload a screenshot — the interviewer will ask targeted questions based on it"
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ mt: 1, alignSelf: 'flex-start' }}><DescriptionIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>
                    }}
                  />
                </Grid>

                {/* Submit */}
                <Grid item xs={12}>
                  <Box sx={{ pt: 1 }}>
                    <Button
                      type="submit" variant="contained" size="large"
                      fullWidth
                      startIcon={<MicIcon />}
                      sx={{
                        py: 1.5, fontSize: '1rem',
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        boxShadow: '0 4px 24px rgba(99,102,241,0.4)',
                        '&:hover': { boxShadow: '0 6px 32px rgba(99,102,241,0.6)' }
                      }}
                    >
                      Start Interview
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
                      Best on Chrome or Edge — uses your microphone for voice responses
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Container>
      </Box>
    </Fade>
  );
}
